/**
 * Main wizard component that orchestrates SIM playback levels and character interactions.
 *
 * @module components/sim/takeSim/SimWizard
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useState, useRef, useCallback} from "react";
import Paper from "@material-ui/core/Paper";
import {makeStyles} from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import Typography from "@material-ui/core/Typography";
import MobileStepper from "@material-ui/core/MobileStepper";
import {connect} from "react-redux";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import RestartIcon from '../../../assets/images/icons/restart.png'
import CloseIcon from "@material-ui/icons/Close";
import PauseIcon from "@material-ui/icons/Pause";
import PlayIcon from "@material-ui/icons/PlayArrow";
import PriorityHighRoundedIcon from "@material-ui/icons/PriorityHighRounded";
import IconButton from "@material-ui/core/IconButton";
import PropTypes from "prop-types";
import {useLocation, useHistory} from "react-router-dom";
import MicIcon from "@material-ui/icons/Mic";
import MicOffIcon from "@material-ui/icons/MicOff";
import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
import PauseCircleFilledIcon from "@material-ui/icons/PauseCircleFilled";
import RealTimeFeedbackDialog from './RealTimeFeedbackDialog.jsx';
import {
    fetchLevelClips,
    updateStats,
    addAudioToClip,
    reset,
    updateCurrentClip,
    restartLevelInTakeSim,
    uploadRecordedVideo,
    userFinalScoreResponse,
    addAssignmentStatus,
    saveVideoBlob,
    logError,
    saveWrapSeconds,
} from "../../../redux/sims/actions";
import Timer from "../Timer";
import {
    uploadAudio,
    getMissedCorrectKeywords,
    getAwsPresignedUrl,
    getUserScriptId,
    requestScreenShareVideo,
} from "../../../services/result.service";
import {
    simWizard,
    simWizardActiveLevelButton,
    simWizardHideLevelButton,
    simWizardWithInternalData
} from "./SimWizard.js";
import ScreenRecording from './ScreenRecording.jsx';
import {Grid} from "@material-ui/core";
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { ReactMic } from 'react-mic';
import {RLCScreenRecorder} from "../../../utils/screenShare";
import WrapUpDialog from "./WrapUpDialog";
import HelpRequestButton from "../../helpdesk/HelpRequestButton";
import {isUrlExpired} from "../../../utils/url";
import {cloneScript} from "../../../utils/scripts";
import CharacterAvatar from "../../common/CharacterAvatar";
import {cryptoJSEncrypt} from "../../../utils/crypto";
import TranscriptService from "../../../utils/TranscriptService";
// DO NOT DELETE - See comment for encryptData function
//const CryptoJS = require("crypto-js");
const useQuery = () => new URLSearchParams(useLocation().search);

const ENCRYPTION_KEY = 'rlc-secret-key@123';

/**
 * SimWizard is a React functional component that acts as the core module for
 * managing and handling the simulation lifecycle in an interactive simulation
 * training environment.
 *
 * The component is designed to manage various states, effects, and functionalities
 * related to user interaction, audio playback, session tracking, data fetching,
 * recording, real-time feedback, and configuration handling.
 *
 * @param {function} fetchLevelClips - Function for fetching clips or scripts
 *      associated with a specific level.
 * @param {object} levelClips - Object containing data about the current level's
 *      audio and video clips, scripts, and configuration.
 * @param {array} levels - Array of all simulation levels.
 * @param {string} simId - The unique identifier for the current simulation session.
 * @param {function} updateStats - Function to update or log session stats
 *      (e.g., started, completed).
 * @param {function} handleViewState - Function to handle user view state
 *      transitions.
 * @param {function} addAudioToClip - Function to add or associate audio
 *      recordings to simulation clips.
 * @param {object} userResponse - Object containing details about the user's
 *      responses and mode of interaction.
 * @param {function} reset - Function to reset the simulation session or state.
 * @param {string} simTitle - Title of the current simulation session.
 * @param {function} updateCurrentClip - Function to update the state for the
 *      active or current clip.
 * @param {function} restartLevelInTakeSim - Function to restart the current
 *      level during the simulation.
 * @param {Object} caseValuesArray - Object storing essential values related to
 *      cases.
 * @param {number} elapsedTime - The total elapsed time of the simulation
 *      session in seconds.
 * @param {function} userFinalScoreResponse - Function to handle the user's
 *      final score and related response data.
 * @param {function} logError - Function for logging or handling errors
 *      encountered during the simulation.
 * @param {object} captivateWindowRef - Reference to the Captivate player
 *      window or element.
 * @param {string} simProductType - Type of simulation product being used
 *      (e.g., "Internal Data", "Salesforce").
 * @param {object} config - Configuration object for the simulation session.
 * @param {function} setHasVideo - Function to update the state of whether the
 *      session includes video components.
 */
const SimWizard = ({
    fetchLevelClips,
    levelClips,
    levels,
    simId,
    updateStats,
    handleViewState,
    addAudioToClip,
    userResponse,
    reset,
    simTitle,
    updateCurrentClip,
    restartLevelInTakeSim,
    caseValuesArray,
    elapsedTime,
    userFinalScoreResponse,
    captivateWindowRef,
    simProductType,
    config,
    setHasVideo,
}) => {
    // import css by calling simWizard  function
    const useStyles = simProductType.includes('Internal Data') ?
        (makeStyles((theme) => simWizardWithInternalData(theme))) :
        (makeStyles((theme) => simWizard(theme)));
    const classes = useStyles();
    const query = useQuery();
    const history = useHistory();
    const location = useLocation();
    const [level] = useState(query.get("levelId"));
    const [activeIndex] = useState(query.get("activeIndex"));
    const [activeClip, setActiveClip] = useState(
        activeIndex ? Number(activeIndex) : 0
    );
    const [record, setRecord] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [open, setOpen] = React.useState(false);
    const [wrapupOpen, setWrapupOpen] = React.useState(false);
    const [wrapupStart, setWrapupStart] = React.useState(0);
    const [loader, setLoader] = useState(false);
    const [response, setResponse] = useState({});
    const [isCoach, setIsCoach] = useState(false);
    const [recPerDialog, setRecPerDialog] = useState(false);
    const [recPer, setRecPer] = useState(false);
    const [totalScreens, setTotalScreens] = useState("2");
    const [startAutoPlay, setStartAutoPlay] = useState(false);
    const [presignedUrl, setPresignedUrl] = useState("");
    const [disableNextButton, setDisableNextButton] = useState(false);
    const [disableDoneButton, setDisableDoneButton] = useState(false);
    const [start, setStart] = useState(false);
    const [presignedBool, setPresignedBool] = useState(false);
    const [timer, setTimer] = useState(0);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const openMenu = Boolean(anchorEl);
    const [recordings, setRecordings] = React.useState([]);
    const [realTimeFeedback, setRealTimeFeedback] = React.useState(null);
    const [curSimId, setCurSimId] = React.useState('');
    const [curLevel, setCurLevel] = React.useState('');
    const [uploadPercent, setUploadPercent] = React.useState(0);
    //const [simTransitionStamps, setSimTransitionStamps] = React.useState([]);
    const [isCompleted, setIsCompleted] = React.useState(false);
    const [isReset, setIsReset] = React.useState(false);
    const [useCoach, setUseCoach] = React.useState(null);
    const [done, setDone] = React.useState(false);
    const [continueSimLevel, setContinueSimLevel] = useState(
        query.get("continueSimLevel") === "true"
    );
    const [isDirty, setIsDirty] = useState(false);
    const [allowNavigate, setAllowNavigate] = useState(false);
    const [isStarted, setIsStarted] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);

    const recordRef = useRef(record);
    const isRecordingRef = useRef(isRecording);
    const realTimeFeedbackRef = useRef(realTimeFeedback);
    const rlcRecorderRef = useRef(null);
    const simTransitionStamps = useRef([]);
    const alreadySaved = useRef(false);
    const agentAudioPlayer = useRef(null);
    const audioPlayer = useRef(null);
    const intervalRef = useRef(null);
    const mediaRec = useRef(null);
    const micStream = useRef(null);
    const transcriptions = useRef([]);

    const maxFeedbackLoops = 500;
    let feedbackCounter = 0;

    useEffect(
        () => {
            if (isCompleted && !alreadySaved?.current) {
                alreadySaved.current = true;
                let clips = levelClips.sim_scripts;
                updateStats(
                    curSimId,
                    curLevel,
                    "completed",
                    userResponse.mode,
                    clips[clips.length-1].order,
                    false
                );
            }
        },
        [isCompleted]
    );

    useEffect(() => {
        recordRef.current = record;
        isRecordingRef.current = isRecording;
        realTimeFeedbackRef.current = realTimeFeedback;
    }, [record, isRecording, realTimeFeedback]);

    useEffect(
        () => {
            if (simId && !curSimId) {
                setCurSimId(simId);
            }
        },
        [simId]
    );

    useEffect(
        () => {
            if (levelClips?._id && !curLevel) {
                setCurLevel(levelClips?._id?.toString());
            }
        },
        [levelClips]
    );

    useEffect(
        () => {
            if (simProductType.includes('Salesforce')) {
                // FIXME - The frontend app is served via TLS (secure connection),
                //  code below should not work, as the browser will refuse to
                //  retrieve a non-secure resource.
                const initialSfUrl = 'http://cedarssinai--training.lightning.force.com/lightning';
                window.toSf({
                    type: "newTab", // The type of message you are sending.
                    url: initialSfUrl
                });
            }
        },
        [simProductType]
    );

    useEffect(
        () => {
            fetchLevelClips(level);
            if (userResponse.mode) {
                updateStats(simId, level, "started", userResponse.mode, activeClip, true);
            }
        },
        [level, levels, simId, userResponse.mode]
    );

    useEffect(
        () => {
            getAwsPresignedUrl()
                .then((res) => {
                    return setPresignedUrl(res.data.url);
                })
                .catch((err) => console.error(err));
            setTimer(0);
        },
        [setPresignedUrl, presignedBool]
    );

    useEffect(
        () => {
            if (levelClips?.sim_scripts?.length) {
                if (useCoach) {
                    fetchLevelClips(level, recordings);
                } else {
                    let scripts = levelClips.sim_scripts.filter((item) => item.character.type !== "coach");
                    const newSimScripts = [];
                    for (let i = 0; i < scripts.length; i += 1) {
                        let updatedScript = {...scripts[i]};
                        if ((scripts[i].character.type === "agent") && !scripts[i].agent_recorded_audio) {
                            updatedScript.audio_url = "";
                            updatedScript.agent_recorded_audio = "";
                        }
                        newSimScripts.push(updatedScript);
                    }
                    addAudioToClip({...levelClips, sim_scripts: [...newSimScripts]});
                    if (!continueSimLevel) {
                        setActiveClip(0);
                    }
                    setDisableNextButton(false);
                    setRecordings([]);
                }
            }
        },
        [useCoach]
    );

    useEffect(
        () => {
            if (levelClips?.sim_scripts?.length) {
                for (const script of levelClips.sim_scripts) {
                    if (
                        (script.character.type === "agent") &&
                        script.real_time_feedback &&
                        !script.correctKeywords &&
                        !script.missedKeywords
                    ) {
                        script.correctKeywords = [];
                        script.missedKeywords = [];
                        script.recorded = false;
                        script.audioTemp = script.audio_url;
                    }
                    if (script.character.type === "coach") {
                        setIsCoach(true);
                    }
                }

                if (useCoach === null) {
                    setUseCoach(levelClips.sim_scripts.find((item) => item.character.type === "coach"));
                }

                if (realTimeFeedback === null) {
                    setRealTimeFeedback(!!levelClips.sim_scripts.find((item) => item.real_time_feedback));
                }
            }
        },
        [levelClips]
    );

    /**
     If the level has Screen Recording enabled, display the Screen Recording Modal.
     */
    useEffect(() =>
        {
            if (location.state.screenRecording && !start) {
                if (activeClip !== 0) {
                    setStartAutoPlay(true);
                    setStart(true);
                    setRecPerDialog(false);
                    setRecPer(false);
                } else {
                    setRecPerDialog(true);
                    setStart(true);
                }
            }
            if (!location.state.screenRecording) {
                setStartAutoPlay(true);
            }
        },
        [levelClips, location.state.screenRecording, start]
    );

    useEffect(
        () => {
            if (isReset) {
                setRecordings([]);
                setIsCoach(false);
                setIsReset(false);

            }
        },
        [isReset]
    );

    useEffect(
        () => {
            const unblock = history.block((newLocation/* , action */) => {
                if (allowNavigate) {
                    return true;
                }

                if (newLocation !== location) {
                    let result = window.confirm(
                        "You have unsaved changes. Are you sure you want to leave?"
                    );
                    setAllowNavigate(result);
                    return result;
                }

                return true;
            });
            //console.log('Setting isDirty to ', isDirty);

            return () => {
                unblock();
            }
        },
        [history, isDirty]
    );

    useEffect(() => {
        if (
            (userResponse.mode === "autoplay") &&
            !isRecording &&
            !open &&
            startAutoPlay &&
            (levelClips?.sim_scripts?.length > 0) &&
            (levelClips?.sim_scripts[activeClip]?.character !== null) &&
            (levelClips?.sim_scripts[activeClip]?.character?.type === "agent") &&
            !levelClips?.sim_scripts[activeClip]?.audio_url &&
            !isStarted
        ) {
            setIsStarted(true);
            handleAutoPlayRecordStart(activeClip, false, true);
        }
    }, [userResponse, levelClips, startAutoPlay, activeClip, isRecording, open, isStarted, realTimeFeedback]);


    const handleClose = () => {
        setAnchorEl(null);
    };

    /**
     * Asynchronously adds a transition stamp to the current list of simulation transition stamps.
     *
     * The function accepts a simulation stamp object containing a type and action,
     * and appends a new stamp object to the list of current simulation transition stamps.
     * The new stamp object includes the provided type, action, and a timestamp indicating
     * the current date and time.
     *
     * @param {Object} simStamp - An object representing the simulation stamp.
     * @param {string} simStamp.type - The type of the simulation stamp.
     * @param {string} simStamp.action - The action associated with the simulation stamp.
     * @returns {Promise<void>} A promise that resolves when the operation is complete.
     */
    const addTransitionStamp = async (simStamp) => {
        let stamps = simTransitionStamps.current.slice(0),
            stamp = {
                type: simStamp.type,
                action: simStamp.action,
                stamp: Date.now(),
                character: simStamp.character ?? levelClips.sim_scripts[activeClip].character.type,
                clip: levelClips.sim_scripts[activeClip],
                index: activeClip,
        };

        stamps.push(stamp);
        simTransitionStamps.current = stamps;
        setIsDirty(true);
        //console.log('Added new stamp: %o', stamp);
    };

    /**
     * Fetches feedback by updating the missed and correct keywords for the specified active state.
     * This method also ensures that the audio and grade score are updated accordingly.
     * Retries the feedback fetch process if the data is incomplete or not available yet, within a
     * defined limit.
     *
     * @param {Object} response - The server response object, containing data such as an ID to
     *     fetch the missed and correct keywords.
     * @param {number} activeState - The current active state index to locate and update the
     *     correct simulation script in the level clips.
     * @return {void} No return value.
     */
    function fetchFeedback(response, activeState) {
        if (feedbackCounter++ > maxFeedbackLoops) {
            return;
        }

        if (intervalRef.current) {
            clearTimeout(intervalRef.current);
            intervalRef.current = null;
        }

        intervalRef.current = setTimeout(async () => {
            if (response?.data?.id) {
                const res = await getMissedCorrectKeywords(response.data.id);
                if (res.data.correct_keywords || res.data.missed_keywords) {
                    const newSimScripts = [];

                    for (let i = 0; i < levelClips.sim_scripts.length; i += 1) {
                        let updatedScript;
                        if (i === activeState) {
                            updatedScript = {...levelClips.sim_scripts[activeState]};
                            updatedScript.correctKeywords = res.data.correct_keywords;
                            updatedScript.missedKeywords = res.data.missed_keywords;
                            updatedScript.grade_score = res.data.grade_score;
                        } else {
                            updatedScript = {...levelClips.sim_scripts[i]};
                        }
                        newSimScripts.push(updatedScript);
                    }

                    const newLevelClip = {
                        ...levelClips,
                        sim_scripts: newSimScripts
                    };
                    try {
                        addAudioToClip(newLevelClip);
                    } catch (error) {
                        console.error('Caught error: ', error);
                    }
                    setLoader(false);
                } else {
                    if (intervalRef.current) {
                        clearTimeout(intervalRef.current);
                    }

                    intervalRef.current = setTimeout(
                        () => {
                            fetchFeedback(response, activeState);
                        },
                        1000
                    );
                }
            }
        }, 0);
    }

    /**
     * Handles the behavior for the "Exit" button within the simulation environment.
     * This function manages various states, stops recording if necessary,
     * performs cleanup tasks, and navigates the user to a new location.
     * Specifically:
     * - If the active clip is the last one, it calls the `handleDoneButton` function.
     * - If recording is in progress, it halts the recording.
     * - Resets playback and recording-related states.
     * - Closes the captivate window if it's open.
     * - Navigates back to the simulations page with specific query parameters.
     *
     * @async
     * @function handleExitButton
     * @returns {Promise<void>} Resolves when all operations, including navigation, are complete.
     */
    const doExit = useCallback(async () => {
        if (activeClip === levelClips.sim_scripts.length -1) {
            return await handleDoneButton(simId, levelClips.sim_scripts[activeClip]._id);
        }

        if (recPer === true) {
            await stopRecording(true);
        }

        setStart(false);
        setStartAutoPlay(false);
        setIsRecording(false);
        setOpen(false);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        if (Object.keys(captivateWindowRef).length !== 0 && captivateWindowRef.constructor === Object) {
            captivateWindowRef.close();
        }
        reset();

        const newLocation = {
            pathname: '/sims',
            search: `?exit=${true}&type=${location.state.type}&assigned_to_id=${userResponse.user_id}`,
            state: {
                sim_id: simId,
                assigned_to_id: userResponse.user_id,
                assignment_id: location.state.assignment.assignment_id,
            }
        };

        history.push(newLocation);
    });


    /**
     * Asynchronous handler function for the "Exit" button.
     *
     * This function performs the following actions:
     * 1. Resets the "isDirty" state to `false`, indicating that there are no unsaved changes.
     * 2. Sets the "allowNavigate" state to `true`, enabling navigation.
     * 3. Initiates a delayed execution of the `doExit` function after a timeout of 500
     * milliseconds.
     *
     * The `setTimeout` delay ensures any required cleanup or state updates
     * are completed before executing the `doExit` function.
     */
    const handleExitButton = async () => {
        setIsDirty(false);
        setAllowNavigate(true);

        for await (const transcription of transcriptions.current) {
            if (transcription) {
                transcription.stop();
            }
        }
        mediaRec.current = null;
        micStream.current = null;

        setTimeout(
            doExit,
            500
        );
    };

    /**
     * Asynchronous function to handle the completion of a simulation.
     *
     * This function finalizes and cleans up the state and resources upon the
     * conclusion of the simulation. It calculates the total wrap-up time, logs
     * transition stamps, and updates relevant states and statistics. Additionally,
     * it manages recording cleanup and resets necessary components.
     *
     * Key functionalities:
     * - Calculates and saves the wrap-up duration.
     * - Adds a transition stamp for the simulation end event.
     * - Marks the simulation as completed.
     * - Stops any ongoing recording processes if active.
     * - Resets the simulation state and updates related UI components.
     * - Logs the count of open WebSocket connections for debugging purposes.
     *
     * @async
     * @function handleSimDone
     */
    const handleSimDone = async () => {
        const endWrapup = Date.now();
        let wrapSeconds = Math.abs((endWrapup - wrapupStart)/1000);

        await addTransitionStamp({type: 'sim', action: 'end', character: 'agent'});
        saveWrapSeconds(wrapSeconds);

        setIsCompleted(true);

        if (recPer) {
            await stopRecording();
            setRecPer(false);
        }

        setWrapupOpen(false);
        handleViewState(true);
        reset();
        console.log('%d WebSockets open', transcriptions.current.length);
    };

    /**
     * Handles the "Done" button functionality in the simulation process.
     * This function performs several actions, including updating the playback
     * and session state, sending data to a remote location, managing level navigation,
     * and handling feedback logic.
     *
     * @async
     * @function handleDoneButton
     * @param {string} simId - The simulation ID associated with the current session.
     * @param {Object} level - The level object or data related to the current simulation level.
     */
    const handleDoneButton = async function(/* simId, level */) {
        setStartAutoPlay(false);
        setWrapupStart(Date.now());
        await addTransitionStamp({type: 'wrapup', action: 'start', character: 'agent'});

        // DO NOT REMOVE THESE. Read the comment for the encryptData function
        //const sessionId = encryptData();
        //const sfRequestData = {type: "completed", sessionId};
        const sessionId2 = await cryptoJSEncrypt(
            JSON.stringify(`${userResponse._id}-${simId}`),
            ENCRYPTION_KEY
        );
        const sfRequestData = {type: "completed", sessionId2};

        window.toSf(sfRequestData);
        if ((Object.keys(captivateWindowRef).length !== 0) && (captivateWindowRef.constructor === Object)) {
            captivateWindowRef.close();
        }

        setOpen(false);
        setDone(true);
        setWrapupOpen(true);
    };

    /**
     * Asynchronously saves transcribed audio data to the database.
     *
     * This function uploads audio data along with metadata such as the user response ID,
     * script ID, and transcribed text to the server using a `FormData` object.
     *
     * @param {string} userResponseId - The unique identifier of the user's response.
     * @param {string} scriptId - The ID of the script associated with the response.
     * @param {Blob} blob - The audio data to be saved.
     * @param {string} transcribe_text - The text transcribed from the audio data.
     * @returns {Promise<object>} A promise that resolves with the response from the server after
     *     successfully saving the data.
     */
    const saveTranscribeToDB = async (userResponseId, scriptId, blob, transcribe_text) => {
        const formData = new FormData();
        formData.append("sim_level_user_response", userResponseId);
        formData.append("script_id", scriptId);
        formData.append("audio_blob", blob);
        formData.append("transcribed_text", transcribe_text);

        return await uploadAudio(formData);
    };

    /**
     * Handles the start of recording with optional simulation and manages various states and
     * streams involved in the process.
     *
     * This asynchronous function sets up the recording environment, initializes audio streams,
     * handles real-time feedback, and interacts with WebSocket connections for audio streaming
     * and transcription. It also manages UI state updates and ensures proper resource cleanup
     * at the end of the recording or in the event of errors.
     *
     * @async
     * @function handleAutoPlayRecordStart
     * @param {number} active - The index of the currently active simulation or clip.
     * @param {boolean} loading - A flag indicating whether the application is in a loading state.
     * @param {boolean} simStart - Indicates whether the simulation has started in simulation mode.
     *
     * @throws {Error} Throws errors related to WebSocket, media stream setup, or audio recording
     *     process.
     *
     * @description Key functionality includes:
     *  - Setting up a recording environment with audio streams.
     *  - Managing transitions, playback states, and WebSocket interactions for real-time
     *     transcription.
     *  - Handling cleanup when recording stops or errors occur.
     *  - Updating UI state and managing logic for simulations based on active state.
     *
     * This function ensures complete lifecycle management for the recording process, including
     *     initialization, real-time feedback, and cleanup.
     */
    const handleAutoPlayRecordStart = async (active, loading, simStart = false) => {
        console.log('handleAutoPlayRecordStart called: (%o, %o, %o)', active, loading, simStart);

        await addTransitionStamp({
            type: simStart ? 'sim' : 'recording',
            action: 'start',
            character: ''
        });

        setRecord(true);
        setIsRecording(true);

        const onTranscriptReady = async (transcription, blobs) => {
            clearInterval(interval);
            setDisableNextButton(false);
            setTimer(time);
            if (time > 10) {
                setPresignedBool(!presignedBool)
            }

            // Call Lambda grading function here.
            // Change below to save grade and transcription to the db instead of
            // calculating it in the back end
            //console.log('Partial = %s', partial);
            console.log('transcription = %s', transcription);
            console.log('blobs = %o', blobs);
            let result = await saveTranscribeToDB(
                userResponse._id,
                levelClips.sim_scripts[activeState]._id,
                new Blob(blobs, {type: "audio/webm"}),
                transcription
            );
            if (result?.data?.success) {
                if (agentAudioPlayer?.current) {
                    agentAudioPlayer.current.src = result.data.audio_url;
                }

                const newSimScripts = levelClips.sim_scripts.map((script, i) =>
                    i === activeState
                        ? { ...script, audio_url: result.data.audio_url, audioTemp: result.data.audio_url, recorded: true }
                        : script
                );
                addAudioToClip({ ...levelClips, sim_scripts: newSimScripts });
                setIsTranscribing(false);

                if (transcriptions.current[activeClip]) {
                    await transcriptions.current[activeClip].stop();
                }
                transcriptions.current[activeClip] = null;
            }

            setResponse(response);
            setRecordings([...recordings, result.data.id]);

            if (activeState === levelClips.sim_scripts.length - 1) {
                setDisableDoneButton(false);
            }

            levelClips.sim_scripts[activeState].transcription = transcription;
            if (
                //  userResponse.mode === "autoplay" &&
                realTimeFeedbackRef.current &&
                //!open &&
                (levelClips.sim_scripts[activeState].character.type === "agent") &&
                levelClips.sim_scripts[activeState].real_time_feedback &&
                levelClips.sim_real_time_feedback &&
                ((levelClips.sim_scripts[activeState].keywords.length > 0) || levelClips.sim_scripts[activeState].natural_lenguage)
            ) {
                setOpen(true);
                fetchFeedback(result, activeState);
            } else {
                if (loading) {
                    setLoader(true);
                }
            }
        }

        let time = timer;

        if (isPlaying) {
            if (agentAudioPlayer.current) {
                agentAudioPlayer.current.pause();
            }
            if (agentAudioPlayer.current) {
                await agentAudioPlayer.current.pause();
            }
            setIsPlaying(false);
        }

        let activeState = active;
        if (activeState === levelClips.sim_scripts.length - 1) {
            setDisableDoneButton(true);
        }

        // if (levelClips.sim_scripts[activeState].real_time_feedback && levelClips.sim_real_time_feedback)
        setDisableNextButton(true);
        activeState = activeState === undefined ? 0 : activeState;

        if (
            levelClips?.sim_scripts?.length &&
            (activeClip > 0) &&
            (activeClip < levelClips?.sim_scripts?.length) &&
            levelClips.sim_scripts[activeClip]
        ) {
            if (
                (levelClips.sim_scripts[activeClip].character.type === "agent") &&
                (levelClips.sim_scripts[activeClip-1].character.type === "agent") &&
                transcriptions.current[activeClip-1]
            ) {
                transcriptions.current[activeClip-1].stop();
            }
        }

        const onError = async (error) => {
            console.error('WS Error = %o', error);
            console.error('error time' + new Date());
            logError({'logError': 'Error on the frontend caused by WebSocket: ' + error.Message});
            alert('There was a problem during this recording please retry');
            setIsTranscribing(false);

            if (!isReset) {
                await handleRetryOnFailure();
            }
            if (done) {
                for await (const transcription of transcriptions.current) {
                    transcription.stop();
                }
                transcriptions.current = [];
            }
        }

        let interval = setInterval(
            () => {
                time += 1;
            },
            1000
        );

        let transcriptService;
        if (!transcriptions.current[activeClip]) {
            transcriptService = await TranscriptService.createTranscriptService(
                presignedUrl,
                onTranscriptReady,
                onError,
                setPresignedUrl
            );
            transcriptions.current[activeClip] = transcriptService;
        } else {
            transcriptService = transcriptions.current[activeClip];
        }
        mediaRec.current = transcriptService.audioStream;
        micStream.current = transcriptService.micStream;
        transcriptService.start();
    };

    /**
     * An asynchronous function that handles updates and changes in simulation scripts
     * when triggered. The function processes the simulation scripts, checks and updates
     * audio URLs, resets keywords and recorded flags, and updates the simulation level
     * with the new data.
     *
     * The function performs the following tasks:
     * - Adds a transition stamp indicating a "change" action with the type "start".
     * - Iterates through simulation scripts, and for the active clip:
     *   - Updates the audio URL if necessary.
     *   - Resets `correctKeywords`, `missedKeywords`, and `recorded` properties.
     * - Pushes updated script data for both active and inactive clips into a new array.
     * - Constructs a new level clip object containing the updated simulation scripts.
     * - Executes an additional function to add audio to the updated clip.
     *
     * The function modifies state variables like `setContinueSimLevel` where required.
     *
     * @function
     * @async
     */
    const handleChange = async () => {
        await addTransitionStamp({type: 'change', action: 'start'});
        //debug('handleChange');
        const newSimScripts = [];

        for (let i = 0; i < levelClips.sim_scripts.length; i += 1) {
            let updatedScript;

            if (i === activeClip) {
                updatedScript = {...levelClips.sim_scripts[activeClip]};
                if (updatedScript.audio_url === "" && updatedScript.audioTemp !== "") {
                    updatedScript.audio_url = updatedScript.audioTemp;
                    setContinueSimLevel(false);
                }
                updatedScript.correctKeywords = [];
                updatedScript.missedKeywords = [];
                updatedScript.recorded = false;
            } else {
                updatedScript = {...levelClips.sim_scripts[activeClip]};
            }

            newSimScripts.push(updatedScript);
        }

        if (micStream.current.status === "recording") {
            if (transcriptions.current[activeClip]) {
                transcriptions.current[activeClip].stop();
            }
            micStream.current = null;
        }

        const newLevelClip = {...levelClips, sim_scripts: newSimScripts};
        addAudioToClip(newLevelClip);
        await addTransitionStamp({type: 'change', action: 'end'});
    };

    /**
     * Handles the retry logic when a failure occurs during the recording or simulation process.
     *
     * This function performs the following tasks:
     * - Adds a transition stamp indicating the retry action has started.
     * - Clones and updates simulation scripts for level clips; specifically, it modifies the
     * active clip while keeping others the same.
     * - Updates the level clips with the newly cloned simulation scripts.
     * - Adds audio to the updated level clip.
     * - Stops and clears any ongoing recording process, resetting relevant state variables and
     * references.
     * - Checks if the presigned URL used for uploading is expired; if expired, retrieves a new
     * presigned URL from the server and updates the state.
     * - Initiates a retry of the auto-play recording process if the current mode is set to
     * "autoplay" and the active clip's character type is an agent.
     *
     * The function contains asynchronous operations to ensure proper handling of asynchronous
     * calls like obtaining new presigned URLs and stopping the recording process.
     */
    const handleRetryOnFailure = async () => {

        await addTransitionStamp({type: 'retry', action: 'start', character: 'agent'});
        //debug('handleRetryOnFailure');
        const newSimScripts = [];

        for (let i = 0; i < levelClips.sim_scripts.length; i += 1) {
            let updatedScript;

            if (i === activeClip) {
                updatedScript = cloneScript(levelClips.sim_scripts[activeClip]);
            } else {
                updatedScript = {...levelClips.sim_scripts[i]};
            }

            newSimScripts.push(updatedScript);
        }

        const newLevelClip = {...levelClips, sim_scripts: newSimScripts};
        addAudioToClip(newLevelClip);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setRecord(false);
        if (mediaRec?.current && transcriptions.current[activeClip]) {
            transcriptions.current[activeClip].stop();
        }

        setIsRecording(false);
        if (isUrlExpired(presignedUrl)) {
            getAwsPresignedUrl()
                .then((res) => {
                    //debug('New presigned URL retrieved');
                    return setPresignedUrl(res.data.url);
                })
                .catch((err) => console.error('Error retrieving new presigned URL: %o', err));
        }

        if (
            (userResponse.mode === 'autoplay') &&
            (levelClips.sim_scripts[activeClip].character.type === "agent")
        ) {
            await handleAutoPlayRecordStart(activeClip, true);
        }
    };

    /**
     * Handles the action of navigating back to the previous clip.
     *
     * This asynchronous function performs several operations when navigating back:
     * 1. Adds a transition stamp indicating the start of the "back" action.
     * 2. Updates statistics related to the simulation, level, and user response for the current
     * state.
     * 3. If there is a previous clip available:
     *    - Checks whether the current active clip lacks an audio URL but has a temporary audio
     * file, and handles changes if required.
     *    - Updates the active clip index to point to the previous clip.
     *
     * Dependencies:
     * - Requires `addTransitionStamp` to log the transition.
     * - Relies on `updateStats` for updating current simulation statistics.
     * - Uses `levelClips` to access simulation scripts.
     * - Calls `handleChange` as needed for managing audio discrepancies in clips.
     *
     * Preconditions:
     * - The `simId`, `level`, `userResponse`, `activeClip`, and `levelClips` variables must be
     * correctly initialized and accessible in scope.
     * - `handleChange` should be implemented to handle necessary adjustments for audio
     * inconsistencies.
     *
     * Postconditions:
     * - The active clip index is updated to the previous clip if a valid clip exists.
     * - Transition stamp and statistics are updated to reflect the backward navigation.
     */
    const handleBackChange = async () => {
        //await addTransitionStamp({type: 'back', action: 'start'});
        //debug('handleBackChange');
        updateStats(simId, level, "started", userResponse.mode, activeClip - 1, true, true);
        if (activeClip > 0) {
            if (
                !levelClips.sim_scripts[activeClip].audio_url &&
                levelClips.sim_scripts[activeClip].audioTemp
            ) {
                await handleChange();
            }
            setActiveClip(activeClip - 1);
        }
        await addTransitionStamp({type: 'back', action: 'end'});
    };

    /**
     * @function encryptData
     * @description Encrypts a concatenated string of user ID and simulation ID using AES
     *     encryption.
     *
     * This function uses the `CryptoJS.AES.encrypt` method to securely encrypt data.
     * The string to be encrypted combines the user ID (`userResponse._id`) and a simulation ID
     *     (`simId`) separated by a hyphen. The encrypted data is then converted into a string
     *     format.
     *
     * @returns {string} The encrypted string.
     */
    /*
        DO NOT DELETE - Moving the react scripts to version 5 took away the use of polyfills,
         including NodeJS's crypto. This means to either import CryptoJS (which has not been updated
         in years) or attempt to implement the polyfill ourselves (which is the route taken).
         If the inteface with SalesForce stops working, then we will have to use CryptoJS and
         uncomment this function and the lines of code that call it.
     */
    /* const encryptData = () => CryptoJS.AES.encrypt(
        JSON.stringify(`${userResponse._id}-${simId}`), ENCRYPTION_KEY
    ).toString(); */

    /**
     * Asynchronous function to handle the transition to the next clip or step in the simulation.
     * This function manages the workflow logic, updates the simulation state, triggers necessary
     * feedback or recording processes, and controls playback operations.
     *
     * The logic includes:
     * - Adding a timestamp for transition tracking.
     * - Encrypting data and sending specific configurations based on the active clip index.
     * - Updating the simulation's statistical data, either for the current clip or advancing to
     * the next.
     * - Handling automatic playback and recording start based on the simulation mode and
     * conditions.
     * - Managing real-time feedback and agent audio interactions, ensuring that recorded or
     * generated feedback is processed and relevant user interfaces are updated.
     * - Advancing the clip index or ending playback when certain criteria are met.
     *
     * This function is critical for managing the flow of the simulation smoothly, ensuring proper
     * transitions, feedback integration, and handling asynchronous interactions efficiently.
     *
     * @async
     * @function
     * @returns {Promise<void>} A Promise that resolves when the transition and related processes
     *     are completed.
     */
    const handleNextChange = async () => {
        console.log('handleNextChange');
        //await addTransitionStamp({type: 'next', action: 'start'});

        if (activeClip === 0) {
            // DO NOT REMOVE THESE. Read the comment for the encryptData function
            //const sessionId = encryptData();
            //const sfRequestData = {type: "sessionId", sessionId};
            const sessionId2 = await cryptoJSEncrypt(
                JSON.stringify(`${userResponse._id}-${simId}`),
                ENCRYPTION_KEY
            );
            const sfRequestData = {type: "sessionId", sessionId2};
            window.toSf(sfRequestData);
        }

        setOpen(false);
        if (activeClip === levelClips.sim_scripts.length - 1) {
            updateStats(simId, level, "started", userResponse.mode, activeClip, true, true);
        } else {
            updateStats(simId, level, "started", userResponse.mode, activeClip + 1, true, true);
            if (
                (userResponse.mode === "autoplay") &&
                (levelClips.sim_scripts[activeClip + 1].character.type === "agent") &&
                !levelClips.sim_scripts[activeClip + 1].agent_recorded_audio
            ) {
                await new Promise((resolve) => setTimeout(resolve, 500));
                if (mediaRec?.current) {
                    console.log('Resetting mediaRec and Web socket');
                    if (transcriptions.current[activeClip-1]) {
                        transcriptions.current[activeClip-1].stop();
                    }
                    mediaRec.current = null;
                    micStream.current = null;
                }
                console.log('isStarted = ', isStarted);

                setActiveClip(activeClip + 1);
                setIsStarted(false);
                await handleAutoPlayRecordStart(activeClip + 1, true);
            }

            if (
                realTimeFeedback &&
                levelClips.sim_scripts[activeClip].real_time_feedback &&
                levelClips.sim_real_time_feedback &&
                !levelClips.sim_scripts[activeClip].agent_recorded_audio &&
                (levelClips.sim_scripts[activeClip].character.type === "agent")
            ) {
                if (
                    continueSimLevel &&
                    levelClips.sim_scripts[activeClip].agent_recorded_url
                ) {
                    levelClips.sim_scripts[activeClip].recorded = true;
                }

                if (
                    levelClips.sim_scripts[activeClip].audio_url &&
                    levelClips.sim_scripts[activeClip].recorded
                ) {
                    setOpen(true);
                    const userScriptId = await getUserScriptId(
                        userResponse._id,
                        levelClips.sim_scripts[activeClip]._id
                    );

                    let result;
                    if (
                        userScriptId &&
                        userScriptId.data &&
                        userScriptId.data.user_script_id
                    ) {
                        result = {data: {id: userScriptId?.data.user_script_id}};
                    }

                    if ((levelClips.sim_scripts[activeClip].keywords.length > 0) || (levelClips.sim_scripts[activeClip].tests.length > 0)) {
                        setLoader(true);
                        fetchFeedback(result ? result : response, activeClip);
                    }

                    await handleChange();
                } else if (
                    !levelClips.sim_scripts[activeClip].audio_url &&
                    levelClips.sim_scripts[activeClip].audioTemp
                ) {
                    await handleChange();
                    setActiveClip(activeClip + 1);
                } else {
                    setActiveClip(activeClip + 1);
                }
            } else {
                setActiveClip(activeClip + 1);
            }
        }
        if (isPlaying) {
            if (agentAudioPlayer.current) {
                await agentAudioPlayer.current.pause();
            }
            setIsPlaying(false);
        }

        await addTransitionStamp({type: 'next', action: 'end'});
    };

    /**
     * Handles the stop action for a recording process.
     *
     * This asynchronous function executes a series of steps to stop a recording,
     * reset associated states, and initiate necessary workflows based on certain conditions.
     *
     * Functionality includes:
     * - Adding a transition stamp indicating the end of the recording.
     * - Ensuring the `rec` object stops recording if it exists.
     * - Resetting recording-related states such as `stream.current` and `isRecording`.
     * - Evaluating conditions such as real-time feedback settings, user mode, and
     *   clip properties to determine the next actions. Depending on the conditions:
     *   - Opens a specific interface for feedback if applicable.
     *   - Proceeds to the next action in autoplay mode if required.
     * - Updates the current active clip based on its status after recording stops.
     *
     * @async
     * @function handleRecordStop
     * @returns {Promise<void>} Resolves when the recording stop process is complete.
     */
    const handleRecordStop = async () => {
        await addTransitionStamp({type: 'record', action: 'end', character: 'agent'});
        setRecord(false);
        if (mediaRec?.current) {
            await mediaRec.current.stop();
        }

        if (
            //userResponse.mode === "autoplay" &&
            realTimeFeedback &&
            levelClips.sim_scripts[activeClip].character.type === "agent" &&
            levelClips.sim_scripts[activeClip].real_time_feedback &&
            levelClips.sim_real_time_feedback &&
            !levelClips.sim_scripts[activeClip].agent_recorded_audio
        ) {
            setOpen(true);
        } else if (userResponse.mode === "autoplay") {
            await handleNextChange();
        }

        setDisableNextButton(false);
        updateCurrentClip(levelClips.sim_scripts[activeClip]);

        setIsTranscribing(true);
        setIsRecording(false);
        //setTimeout(() => setIsRecording(false), 0);
    };

    // when audio already recorded handle play button
    /**
     * Asynchronously plays the recorded audio using the current audio player instance.
     *
     * - Adds a transition stamp with the type 'audio-check' and action 'start' before playback
     * begins.
     * - Sets the playback state to 'playing' when the audio starts.
     * - When the audio playback ends, a transition stamp with the type 'audio-check' and action
     * 'end' is added, and the playback state is set to 'not playing'.
     *
     * Prerequisites:
     * - `audioPlayer` must be a valid reference to an audio player with a `current` object.
     * - `setIsPlaying` must be a function that updates the playback state.
     * - `addTransitionStamp` must be a function that accepts an object with `type` and `action`
     * properties for logging transitional states.
     *
     * Behavior:
     * - Ensures smooth transition stamps for logging audio playback events as it starts and
     * finishes.
     */
    const playRecordedAudio = async () => {
        await addTransitionStamp({type: 'audio-check', action: 'start', character: 'agent'});

        if (audioPlayer && audioPlayer.current) {
            audioPlayer.current.play();
        }
        setIsPlaying(true);

        //  when audio playback ends, set isPlaying to false
        if (audioPlayer && audioPlayer.current) {
            audioPlayer.current.onended = async () => {
                setIsPlaying(false);
                await addTransitionStamp({type: 'audio-check', action: 'end', character: 'agent'});
            }
        }
    };

    // when audio already recorded handle pause button
    /**
     * Pauses the currently playing recorded audio.
     *
     * This asynchronous function pauses the audio playback if the audio player is active.
     * It also records a transition stamp with the type set to 'replay' and action set to 'pause'.
     * After pausing the audio, it updates the playback state to indicate the audio is not playing.
     */
    const pauseRecordedAudio = async () => {
        await addTransitionStamp({type: 'replay', action: 'pause'});
        if (audioPlayer.current) {
            audioPlayer.current.pause();
        }

        setIsPlaying(false);
    };

    // play script audio (other than agent)
    /**
     * Asynchronously plays audio using the `audioPlayer` reference and updates playback state.
     *
     * The function initiates playback by marking the start of the audio using `addTransitionStamp`.
     * It sets the `isAudioPlaying` state to `true` and assigns an event listener to handle the
     * audio playback completion. When the playback ends, it updates the `isAudioPlaying` state to
     * `false`, and if in autoplay mode, progresses to the next audio clip by invoking
     * `handleNextChange` and updates the current clip information.
     *
     * Dependencies:
     * - `audioPlayer`: Reference to the audio player element.
     * - `setIsAudioPlaying`: State updater function to track if audio is playing.
     * - `addTransitionStamp`: Asynchronous function for tracking playback transition.
     * - `userResponse`: Object that contains playback mode information (e.g., autoplay).
     * - `levelClips`: Data structure containing available audio clip information.
     * - `activeClip`: Index or identifier of the currently active audio clip.
     * - `updateCurrentClip`: Function to update playback with the current clip's details.
     * - `handleNextChange`: Function invoked for advancing to the next audio clip in autoplay mode.
     */
    const playAudio = async () => {
        await addTransitionStamp({type: 'play', action: 'start'});
        if (audioPlayer && audioPlayer.current) {
            audioPlayer.current.play();
        }
        setIsAudioPlaying(true);
        //  when audio playback ends, set isPlaying to false
        if (audioPlayer && audioPlayer.current) {
            audioPlayer.current.onended = async () => {
                setIsAudioPlaying(false);
                if (userResponse && userResponse.mode === "autoplay") {
                    await handleNextChange();
                }
                updateCurrentClip(levelClips.sim_scripts[activeClip]);
            }
        }
    };

    /**
     * Represents an asynchronous function that initiates an audio change process.
     * The function performs the following actions:
     * 1. Adds a transition stamp of type 'audiochange' with the action 'start'.
     * 2. Plays the audio using an asynchronous operation.
     *
     * This function is commonly used to handle audio transitions with related state tracking.
     *
     * @function
     * @async
     * @returns {Promise<void>} A promise that resolves when the audio change process is completed.
     */
    const audioChange = async () => {
        await addTransitionStamp({type: 'audiochange', action: 'start'});
        await playAudio();
    };

    /**
     * Asynchronous function that pauses audio playback and updates the application's state.
     *
     * This function adds a transition stamp indicating
     * the start of the "pauseaudio" action. If an audio
     * player is present and currently playing, it pauses
     * the audio playback. It also updates the application
     * state to reflect that the audio is no longer playing.
     *
     * Dependencies:
     * - Requires the `addTransitionStamp` function to log the type and action.
     * - Assumes `audioPlayer` is an object with a `current` property pointing to the audio element.
     * - Relies on a state setter function `setIsAudioPlaying` to update state.
     */
    const pauseAudio = async () => {
        await addTransitionStamp({type: 'pauseaudio', action: 'start'});
        if (audioPlayer && audioPlayer.current) {
            audioPlayer.current.pause();
        }

        setIsAudioPlaying(false);
    };

    /**
     * Renders the appropriate play, pause, or notification button based on the given audio and
     * script state.
     *
     * This function determines which button to display by checking the availability of an audio
     * URL, the current mode of user interaction (e.g., "autoplay" or "practice"), and the position
     * of the currently active clip within the simulation's script. Depending on these factors, it
     * returns a corresponding `IconButton` element with appropriate event handling logic.
     *
     * @param {boolean} audioPlay - Indicates whether audio is currently playing.
     * @param {Object} script - Represents the script object associated with the current
     *     simulation.
     * @param {string} script.audio_url - The URL for the audio associated with the script, if
     *     available.
     * @returns {React.Element} The appropriate button (`PlayIcon`, `PauseIcon`, or
     *     `PriorityHighRoundedIcon`) depending on the given state and conditions.
     */
    const showPlayButton = (audioPlay, script) => {
        if (audioPlay && script.audio_url) {
            return (
                <IconButton
                    color="primary"
                    aria-label="pause"
                    component="span"
                    size="small"
                    className={
                        activeClip === levelClips.sim_scripts.length - 1 && userResponse.mode === "autoplay" ?
                            classes.pauseAutoplayButton :
                            classes.pauseButton
                    }
                    onClick={() => pauseAudio()}
                >
                    <PauseIcon/>
                </IconButton>
            )
        }

        if (script.audio_url) {
            return (
                <IconButton
                    color="primary"
                    aria-label="pause"
                    component="span"
                    size="small"
                    onClick={() => playAudio()}
                    className={activeClip === levelClips.sim_scripts.length - 1 && userResponse.mode === "autoplay" ?
                        classes.pauseAutoplayButton :
                        classes.pauseButton
                    }
                >
                    <PlayIcon/>
                </IconButton>
            );
        }

        if (
            userResponse.mode === "practice" ||
            (userResponse.mode === "autoplay" && activeClip === levelClips.sim_scripts.length - 1)
        ) {
            return (
                <IconButton
                    color="primary"
                    aria-label="no audio"
                    component="span"
                    size="small"
                    className={activeClip === levelClips.sim_scripts.length - 1 && userResponse.mode === "autoplay" ?
                        classes.pauseAutoplayButton :
                        classes.pauseButton
                    }
                    onFocus={handleNextChange}
                >
                    <PriorityHighRoundedIcon/>
                </IconButton>
            );
        }

        handleNextChange();
    };

    /**
     * Renders a script or hint text based on the input conditions.
     *
     * @param {boolean} hideText - Determines whether the main script text should be hidden.
     * @param {boolean} hintText - Indicates whether the hint text is to be displayed.
     * @param {boolean} script - Indicates whether the main script content should be shown.
     * @returns {JSX.Element} A Typography component containing the script or hint text based on
     *     the specified parameters.
     */
    const showScript = (hideText, hintText, script) => {
        let hint_text = false;

        levels.map((lvl, idx) => {
            if (lvl.level_id === level) {
                hint_text = lvl.hint_text;
            }
            return idx;
        });

        if ((hideText && hintText) || (!script && hintText)) {
            return (
                <Typography
                    variant="subtitle1"
                    align="justify"
                    className={classes.scriptTag}
                >
                    {hint_text ?
                        <em className={classes.hintText}>
                            {levelClips.sim_scripts[activeClip].hint_text}
                        </em> : ''
                    }
                </Typography>
            );
        }

        if (script && hintText) {
            return (
                <Typography
                    variant="subtitle1"
                    align="justify"
                    className={classes.scriptTag}
                >
                    {levelClips.sim_scripts[activeClip].script}
                    <br/>
                    {
                        hint_text ?
                            <em className={classes.hintText}>
                                {levelClips.sim_scripts[activeClip].hint_text}
                            </em> : ''
                    }
                </Typography>
            );
        }

        if (hideText) {
            return (
                <Typography
                    variant="subtitle1"
                    align="justify"
                    className={classes.scriptTag}
                >
                    {hint_text ?
                        <em className={classes.hintText}>
                            {levelClips.sim_scripts[activeClip].hint_text}
                        </em> : ''
                    }

                </Typography>
            );
        }

        return (
            <Typography
                variant="subtitle1"
                align="justify"
                className={classes.scriptTag}
            >
                {levelClips.sim_scripts[activeClip].script}
            </Typography>
        );
    };

    /**
     * Asynchronous function to handle the restart operation in a specific context.
     * This function performs a series of tasks such as resetting recording states,
     * stopping ongoing streams, fetching level clips, and managing window state.
     * Additionally, it updates user assignment data and score responses.
     *
     * The function executes the following steps:
     * - Adds a transition stamp with the type 'restart' and action 'start'.
     * - Resets recording states and stops any active recording or streams.
     * - Restarts the level simulation and enables the coach functionality.
     * - Fetches level-specific clips and resets the active clip index.
     * - Closes any open captivate-related windows.
     * - Updates coaching usage state to true.
     * - Sends a payload containing user assignment and score details after reset.
     * - Clears interval references and resets recordings storage.
     *
     * This is typically used in scenarios where user progress or activity needs to be restarted
     * and all associated states need to be reset to their initial configuration.
     */
    const handleRestart = async () => {
        await addTransitionStamp({type: 'restart', action: 'start'});

        setRecord(false);
        if (mediaRec?.current) {
            await mediaRec.current.stop();
        }

        restartLevelInTakeSim();
        setIsRecording(false);
        setOpen(false);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        fetchLevelClips(level, recordings);
        console.log('****** Fetched Clips *******');
        setActiveClip(0);
        if (Object.keys(captivateWindowRef).length !== 0 && captivateWindowRef.constructor === Object) {
            captivateWindowRef.close();
        }
        setUseCoach(true);
        const payload = {
            ...caseValuesArray,
            isPost: true,
            assignment_id: userResponse.assignment_id,
            elapsedTime: elapsedTime,
            wrapupTime: Date.now() - wrapupStart,
            exit_or_restart: true,
        };
        userFinalScoreResponse(userResponse._id, payload);
        setRecordings([]);
    };

    /**
     * Handles the restart of the simulation level without utilizing the coach feature.
     * This function performs several cleanup and resetting operations, ensuring
     * that the application state is appropriately adjusted for a restart without
     * the coach enabled.
     *
     * Operations performed:
     * - Disables the recording feature.
     * - Stops any active recordings using the recording reference (if present).
     * - Resets the media stream to null.
     * - Invokes the restart process for the level in a simulation.
     * - Disables the coach and updates relevant state flags.
     * - Closes any open modal windows and clears the interval timer.
     * - Resets the active clip and disables the coach feature.
     * - Closes the captivate window if it exists.
     *
     * The function relies on several external variables and state setters,
     * such as `setRecord`, `stream`, `restartLevelInTakeSim`, and others,
     * to manage the application state and handle required cleanup processes.
     *
     * This function is asynchronous due to its interaction with an asynchronous
     * stop operation for the recording reference.
     *
     * TODO - What to do with screen recording: Restart everything or just keep
     *      recording?
     * TODO - How to avoid the Wizard being re-rendered more than once when
     *      coachEnabled changes?
     *
     * @async
     * @function handleRestartWithoutCoach
     */
    const handleRestartWithoutCoach = async () => {
        setRecord(false);
        for await (const transcription of transcriptions.current) {
            transcription.stop();
        }
        transcriptions.current = [];

        restartLevelInTakeSim();
        setIsRecording(false);
        setOpen(false);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setActiveClip(0);
        if (Object.keys(captivateWindowRef).length !== 0 && captivateWindowRef.constructor === Object) {
            captivateWindowRef.close();
        }
        setUseCoach(false);
    };

    // Toggling coach
    /**
     * Displays the current state of the "Coach" functionality and provides an option
     * to toggle it between "On" and "Off".
     *
     * If the "useCoach" variable is set to `true`, it renders a UI indicating that the coach
     * is currently enabled with an option to restart without the coach.
     * If "useCoach" is set to `false`, it renders a UI indicating that the coach is disabled
     * with an option to restart with the coach.
     *
     * The display includes a clickable typography element, which triggers the appropriate
     * restart function (`handleRestartWithoutCoach` or `handleRestart`) based on the current state.
     *
     * @function showCoach
     */
    const showCoach = () => {
        let labelText = 'Restart With Coach',
            handler = handleRestart;

        if (useCoach) {
            labelText = 'Restart Without Coach';
            handler = handleRestartWithoutCoach;
        }

        return (
            <div className={classes.characterDiv}>
                <Typography
                    className={classes.characterTypo}
                    onClick={handler}
                >
                    Coach : On
                    <span className={classes.characterSpan}>
                        &nbsp; {labelText}
                    </span>
                </Typography>
            </div>
        );
    };

    /**
     * Handles the change event for a radio input related to real-time feedback.
     *
     * Iterates through the `sim_scripts` array within the `levelClips` object to update the
     * `real_time_feedback` property of elements with a `character.type` of "agent". Creates
     * a new array `newSimScripts` containing the updated or unmodified scripts.
     *
     * Updates `levelClips` with the modified `sim_scripts` array and passes the updated
     * object to the `addAudioToClip` function. Finally, updates the real-time feedback state
     * by determining if any of the scripts in `levelClips.sim_scripts` have `real_time_feedback`
     * enabled.
     *
     * @param {Object} event - The event object from the change handler.
     * @param {Object} event.target - The target element of the event.
     * @param {string} event.target.value - The value of the selected radio option.
     */
    const handleRadioChange = (event) => {
        const newSimScripts = [];
        let updatedScript = {};
        for (let i = 0; i < levelClips.sim_scripts.length; i += 1) {
            if (levelClips.sim_scripts[i].character.type === "agent") {
                updatedScript = levelClips.sim_scripts[i];
                updatedScript.real_time_feedback = (event.target.value === "yes");
                newSimScripts.push(updatedScript);
            } else {
                newSimScripts.push(levelClips.sim_scripts[i]);
            }
        }

        const newLevelClip = {...levelClips, sim_scripts: newSimScripts};
        addAudioToClip(newLevelClip);
        setRealTimeFeedback(!!levelClips.sim_scripts.find((item) => item.real_time_feedback))
    };

    /**
     * Handles the dialog close action and performs the following tasks:
     * - Enables the auto-play functionality.
     * - Disables the recording-per-dialog behavior.
     * - Disables the general recording behavior.
     * - Sets the total number of screens to "2".
     *
     * This function is generally used to reset or configure application states
     * following the closure of a dialog window.
     */
    const dialogClose = () => {
        setStartAutoPlay(true);
        setRecPerDialog(false);
        setRecPer(false);
        setTotalScreens("2");
    };

    /**
     * Stops the recording process and handles optional video upload functionality.
     *
     * @async
     * @function stopRecording
     * @param {boolean} bSkipVideoUpload - A flag indicating whether to skip the video upload
     *     process. If true, the video upload process will not execute.
     * @returns {Promise<void>} Resolves when the recording has been stopped and any optional video
     *     upload has completed.
     *
     * @description
     * This function stops the ongoing recording operation and optionally uploads the recorded
     *     video streams to the server. If `bSkipVideoUpload` is set to false, the function also
     *     performs the following actions:
     * - Calculates the "wrap-up" time for the session.
     * - Adds a transition stamp indicating the "wrapup" end event.
     * - Uploads the video streams to the server, reporting progress percentages.
     * - Sends the recorded video data along with any associated metadata for processing.
     *
     * In case of an error during uploading, the error is logged to the console.
     * All transition stamps are reset after the operation is completed.
     */
    const stopRecording = async (bSkipVideoUpload) => {
        console.log('%d WebSockets open', transcriptions.current.length);

        if (rlcRecorderRef.current) {
            await rlcRecorderRef.current.stop();
            if (!bSkipVideoUpload) {
                let wrapUpTime = Date.now() - wrapupStart;
                await addTransitionStamp({type: 'wrapup', action: 'end'});
                let streams = await rlcRecorderRef.current.uploadStreams((percent) => {
                    setUploadPercent(percent);
                });
                if (streams?.length) {
                    try {
                        await requestScreenShareVideo(
                            streams,
                            wrapUpTime,
                            simTransitionStamps.current.slice(0),
                            userResponse._id.toString()
                        );
                    await rlcRecorderRef.current.clearStreams();
                    } catch (err) {
                        console.error('Uploading of video failed: %o', err);
                    }
                }
            }

            simTransitionStamps.current = [];
        }
    };

    /**
     * An asynchronous function responsible for initiating the recording process.
     * This includes adding a transition stamp for the start of the recording,
     * checking the readiness of the recording instance, and starting autoplay.
     *
     * The function ensures that recording is not started until the recorder
     * reference is in a ready state. If the recorder is not ready, the function
     * recursively calls itself after a delay.
     */
    const doStartRecording = async () => {
        if (rlcRecorderRef.current && !rlcRecorderRef.current.ready) {
            setTimeout(doStartRecording, 500);
            return;
        }

        await addTransitionStamp({type: 'recording', action: 'start'});
        setStartAutoPlay(true);
    };

    /**
     * Initiates the screen recording process.
     * Sets the video recording status to true and starts the recording.
     * If a recorder instance is available, it begins recording using the recorder.
     * Additionally, triggers the necessary setup for starting the recording.
     *
     * @async
     * @function screenRecording
     * @returns {Promise<void>} A promise that resolves when the recording process starts
     *     successfully.
     */
    const screenRecording = async () => {
        setHasVideo(true);

        if (rlcRecorderRef.current) {
            await rlcRecorderRef.current.start();
        }

        await doStartRecording();
    };

    /**
     * Asynchronous function to initialize a screen recorder and start screen recording.
     * The `rlcRecorderRef` refers to a mutable reference object for the screen recorder instance.
     *
     * - Initializes the `RLCScreenRecorder` instance with the specified maximum number of screens.
     * - Prepares the screen recorder for recording.
     * - Starts the screen recording process.
     *
     * @function
     */
    const capture = async () => {
        rlcRecorderRef.current = new RLCScreenRecorder({
            maxScreens: totalScreens,
        });
        await rlcRecorderRef.current.prepare();
        await screenRecording();
    };

    /**
     * Handles the action of changing the current clip by setting the anchor element.
     *
     * @function changeClip
     * @param {Object} event - The event triggered by the user's interaction.
     * @param {Object} event.currentTarget - The element that triggered the event.
     */
    const changeClip = (event) => {
        setAnchorEl(event.currentTarget);
    };

    /**
     * Handles the action to navigate or jump to the next item in a sequence.
     * This function updates various states and logs the progression for tracking purposes.
     *
     * @function
     * @name jumpToNext
     * @param {number} index - The index of the next item to navigate to.
     * @returns {void}
     */
    const jumpToNext = (index) => {
        setAnchorEl(null);
        setActiveClip(index);
        updateStats(
            simId, level, "started", userResponse.mode, index, true, true
        );
    };

    const onMicStop = async (/* evt */) => {
        //console.log('Mic Stop evt = %o', evt);
    }

    const onMicData = async (/* evt */) => {
        //console.log('Mic Data evt = %o', evt);
    }

    if (!("getDisplayMedia" in navigator.mediaDevices)) {
        return (
            <div>
                <Typography variant="h2" gutterBottom>
                    Please upgrade your browser.
                </Typography>
                <Typography variant="h5" gutterBottom>
                    This application requires the WebMedia API, which is supported by all major browsers released in
                    the last couple of years.
                </Typography>
                <Typography>
                    If you are seeing this message and think your browser is up to date, please contact your trainer.
                </Typography>
            </div>
        );
    }

    if (
        levelClips &&
        (levelClips.sim_scripts?.length > 0) &&
        (levelClips.sim_scripts[activeClip].character !== null)
    ) {
        return (
            <div>
                <Paper className={classes.paper}>
                    <HelpRequestButton simData={{sim_id: simId, level: level, step: activeClip}}
                        classes={classes}
                    />
                    <Timer/>

                    <ScreenRecording
                        capture={capture}
                        setRecPerDialog={setRecPerDialog}
                        dialogClose={dialogClose}
                        recPerDialog={recPerDialog}
                        recPer={recPer}
                        setRecPer={setRecPer}
                        totalScreens={totalScreens}
                        setTotalScreens={setTotalScreens}
                        classes={classes}
                    />
                    <div className={classes.mainDiv}>
                        <Grid container>
                            <Grid xs={6} item>
                                <div className={classes.levelDiv}>
                                    <span
                                        className={classes.lvlName}>Level {Number(query.get("order")) + 1} : </span><span>{levelClips.name}</span>
                                </div>
                            </Grid>
                            <Grid xs={6} item>
                                <div className={classes.root}>
                                    {levels.map((l) => (
                                        <div key={l.level_id}>
                                            <Button
                                                key={l.level_id}
                                                variant="contained"
                                                disableElevation
                                                className={classes.levelButton}
                                                style={l.level_id === level
                                                    ? simWizardActiveLevelButton
                                                    : simWizardHideLevelButton}
                                                endIcon={userResponse && userResponse.mode === 'practice' ?
                                                    <KeyboardArrowDownIcon/> : ''}
                                                onClick={userResponse && userResponse.mode === 'practice' ? changeClip : null}
                                            >
                                                <span className={classes.activeClip}>{activeClip + 1} </span>
                                                <span
                                                    className={classes.totalClip}>/ {levelClips.sim_scripts.length}</span>
                                            </Button>
                                            <Menu
                                                anchorEl={anchorEl}
                                                open={openMenu}
                                                onClose={handleClose}
                                                className={classes.menu}
                                            >
                                                {levelClips.sim_scripts.map((script, index) => (
                                                    <MenuItem
                                                        key={script._id}
                                                        selected={index === activeClip}
                                                        className={classes.menuItem}
                                                        onClick={() => jumpToNext(index)}
                                                    >
                                                        {index + 1}
                                                    </MenuItem>
                                                ))}
                                            </Menu>
                                        </div>
                                    ))}
                                </div>
                            </Grid>
                        </Grid>

                        <div
                            className={levelClips.sim_scripts.length < 10 ?
                                classes.mobileRoot :
                                classes.mobileStepper
                            }
                        >
                            <MobileStepper
                                backButton={<></>}
                                nextButton={<></>}
                                variant={levelClips.sim_scripts.length < 10 ? "dots" : "progress"}
                                steps={
                                    levelClips.sim_scripts && levelClips.sim_scripts.length > 0
                                        ? levelClips.sim_scripts.length
                                        : 1
                                }
                                className={classes.mobileProgress}
                                activeStep={activeClip}
                                position="static"
                                classes={{
                                    dots: classes.dots,
                                    root: classes.stepperRoot,
                                    dot: classes.dot,
                                    dotActive: classes.dotActive,
                                }}
                            />
                        </div>
                        <div>
                            <Grid container spacing={2} className={classes.mainInfoDiv}>
                                <Grid item xs={12}>
                                    <div className={classes.avatarDiv}>
                                        <CharacterAvatar
                                            avatar={levelClips.sim_scripts[activeClip].character.avatar}
                                            classes={classes.avatar}
                                            alt="Img"
                                        />
                                    </div>
                                    <div className={classes.characterInfo}>
                                        <Button
                                            variant="outlined"
                                            disabled
                                            className={classes.characterName}
                                        >
                                            {levelClips.sim_scripts[activeClip].character.name} -{"The  "}
                                            {levelClips.sim_scripts[activeClip].character.masked_type
                                                ? levelClips.sim_scripts[activeClip].character.masked_type
                                                : levelClips.sim_scripts[activeClip].character.type}
                                        </Button>
                                    </div>
                                    {isCoach ? showCoach() : null}
                                    {levelClips.sim_real_time_feedback && levelClips.level_real_time_feedback &&
                                    levelClips.sim_scripts[activeClip].character.type === "agent" ? (
                                        <div className={classes.radioDiv}>
                                            <FormControl className={classes.radioBtnDiv} component="fieldset">
                                                <FormLabel className={classes.radioTypo} component="legend">
                                                    Real-time feedback?:
                                                </FormLabel>
                                                <RadioGroup
                                                    defaultValue="yes"
                                                    aria-label="feedback"
                                                    className={classes.radioBtnDiv}
                                                >
                                                    <FormControlLabel
                                                        className={classes.radioBtn}
                                                        value="yes"
                                                        control={
                                                            <Radio
                                                                checked={realTimeFeedback === true}
                                                                value="yes"
                                                                onChange={(e) => handleRadioChange(e)}
                                                                className={classes.radioYes}
                                                                size="small"
                                                            />
                                                        }
                                                        label="Yes"
                                                    />
                                                    <FormControlLabel
                                                        className={classes.radioBtn}
                                                        value="no"
                                                        control={
                                                            <Radio
                                                                checked={realTimeFeedback !== true}
                                                                value="no"
                                                                onChange={(e) => handleRadioChange(e)}
                                                                className={classes.radioNo}
                                                                size="small"
                                                            />
                                                        }
                                                        label="No"
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                        </div>
                                    ) : null}
                                </Grid>
                                <Grid item xs={12} className={classes.scriptGrid}>
                                    {showScript(
                                        levelClips.sim_scripts[activeClip].hide_text,
                                        levelClips.sim_scripts[activeClip].hint_text,
                                        levelClips.sim_scripts[activeClip].script
                                    )}
                                </Grid>
                            </Grid>
                        </div>
                        <div
                            className={
                                userResponse.mode === "practice"
                                    ? classes.nextBackDiv
                                    : classes.nextBackAutoplayDiv
                            }
                        >
                            {userResponse &&
                                (userResponse.mode === "practice") &&
                                !isRecording && (
                                <Button
                                    variant="contained"
                                    disableElevation
                                    className={
                                        activeClip === 0
                                            ? classes.backButton
                                            : classes.activeBackButton
                                    }
                                    onClick={handleBackChange}
                                >
                                    <ArrowBackIosIcon className={classes.arrowBack}/>
                                </Button>
                            )}
                            {startAutoPlay === true && (
                                <div>
                                    {(levelClips.sim_scripts[activeClip].character.type === "agent") &&
                                    !levelClips.sim_scripts[activeClip].agent_recorded_audio ? (
                                        <audio
                                            key={levelClips.sim_scripts[activeClip].audio_url}
                                            ref={audioPlayer}
                                        >
                                            <source
                                                src={levelClips.sim_scripts[activeClip].audio_url}
                                                type="audio/mp3"
                                            />
                                        </audio>
                                    ) : (
                                        <audio
                                            key={levelClips.sim_scripts[activeClip].audio_url}
                                            ref={audioPlayer}
                                            onLoadedData={audioChange}
                                        >
                                            <source
                                                src={levelClips.sim_scripts[activeClip].audio_url}
                                                type="audio/mp3"
                                            />
                                        </audio>
                                    )}
                                </div>
                            )}

                            {/* if character type is agent, record audio */}
                            {(levelClips.sim_scripts[activeClip].character.type === "agent") &&
                            !levelClips.sim_scripts[activeClip].agent_recorded_audio ? (
                                isRecording && startAutoPlay === true ? (
                                    <IconButton
                                        color="primary"
                                        aria-label="stop mic recording"
                                        component="span"
                                        size="medium"
                                        className={classes.recButton}
                                        onClick={handleRecordStop}
                                    >
                                        <MicOffIcon/>
                                    </IconButton>
                                ) : (
                                    <IconButton
                                        color="primary"
                                        aria-label="start mic recording"
                                        component="span"
                                        size="medium"
                                        className={classes.pauseButton}
                                        onClick={() => handleAutoPlayRecordStart(activeClip, false)}
                                        disableFocusRipple={false}
                                    >
                                        <MicIcon/>
                                    </IconButton>
                                )
                            ) : (
                                showPlayButton(isAudioPlaying, levelClips.sim_scripts[activeClip])
                            )}

                            {(activeClip === levelClips.sim_scripts.length - 1) && !isRecording ? (
                                <Button
                                    variant="contained"
                                    disableElevation
                                    className={(isAudioPlaying && levelClips && levelClips.sim_scripts && levelClips.sim_scripts[activeClip])
                                    || disableDoneButton || (levelClips.sim_scripts[activeClip].character.type === "agent" && !levelClips.sim_scripts[activeClip].agent_recorded_audio && agentAudioPlayer.current === null) ?
                                        classes.nextButton : classes.nextButtonWithAnimation}
                                    disabled={disableDoneButton}
                                    onClick={() => handleDoneButton(simId, level, config)}
                                >
                                    {disableDoneButton ? (
                                        <CircularProgress color="primary" size={20}/>
                                    ) : (
                                        "Done"
                                    )}
                                </Button>
                            ) : userResponse && userResponse.mode === "practice" && !isRecording ? (
                                <Button
                                    variant="contained"
                                    disableElevation
                                    className={(isAudioPlaying && levelClips && levelClips.sim_scripts && levelClips.sim_scripts[activeClip]) ||
                                        disableNextButton || (levelClips.sim_scripts[activeClip].character.type === "agent" && agentAudioPlayer.current === null) ?
                                            classes.nextButton :
                                            classes.nextButtonWithAnimation
                                    }
                                    onClick={handleNextChange}
                                    disabled={disableNextButton}
                                >
                                    <ArrowForwardIosIcon className={classes.arrowForward}/>
                                </Button>
                            ) : (
                                ""
                            )}
                        </div>
                        {levelClips.sim_scripts[activeClip].character.type === "agent" &&
                            !levelClips.sim_scripts[activeClip].agent_recorded_audio &&
                            (isTranscribing ||
                                levelClips.sim_scripts[activeClip].audio_url ||
                                (levelClips.sim_scripts[activeClip].agent_recorded_url && continueSimLevel)
                            ) &&
                            userResponse.mode === "practice" &&
                            !isRecording && (
                                <div className={classes.centerElement}>
                                    <audio
                                        key={
                                            continueSimLevel &&
                                            levelClips.sim_scripts[activeClip].agent_recorded_url
                                                ? levelClips.sim_scripts[activeClip].agent_recorded_url
                                                : levelClips.sim_scripts[activeClip].audio_url
                                        }
                                        ref={agentAudioPlayer}
                                    >
                                        <source
                                            src={
                                                continueSimLevel &&
                                                levelClips.sim_scripts[activeClip].agent_recorded_url
                                                    ? levelClips.sim_scripts[activeClip].agent_recorded_url
                                                    : levelClips.sim_scripts[activeClip].audio_url
                                            }
                                            type="audio/mp3"
                                        />
                                    </audio>
                                    {isPlaying ? (
                                        <IconButton
                                            color="primary"
                                            aria-label="pause"
                                            component="span"
                                            size="medium"
                                            disabled={isTranscribing}
                                            onClick={pauseRecordedAudio}
                                        >
                                            <PauseCircleFilledIcon/>
                                        </IconButton>
                                    ) : (
                                        <IconButton
                                            color="primary"
                                            aria-label="play"
                                            component="span"
                                            size="medium"
                                            disabled={isTranscribing}
                                            onClick={playRecordedAudio}
                                        >
                                            <PlayCircleFilledIcon/>
                                        </IconButton>
                                    )}
                                    <span>Play recorded clip</span>
                                </div>
                            )}
                        <div className={classes.centerElement} style={{display: record ? 'flex' : 'none'}}>
                            <ReactMic
                                record={record}
                                className="sound-wave"
                                width={150}
                                strokeColor="#f27e7f"
                                backgroundColor="#fcfcfc"
                                echoCancellation={true}
                                noiseSuppression={true}
                                autoGainControl={true}
                                channelCount={1}
                                onStop={onMicStop}
                                onData={onMicData}
                            />
                        </div>

                        <RealTimeFeedbackDialog
                            open={open}
                            activeClip={activeClip}
                            handleAutoPlayRecordStart={handleAutoPlayRecordStart}
                            loader={loader}
                            disableNextButton={disableNextButton}
                            continueSimLevel={continueSimLevel}
                            agentAudioPlayer={agentAudioPlayer}
                            isPlaying={isPlaying}
                            playRecordedAudio={playRecordedAudio}
                            setOpen={setOpen}
                            intervalRef={intervalRef}
                            pauseRecordedAudio={pauseRecordedAudio}
                            handleViewState={handleViewState}
                            setStart={setStart}
                            recPer={recPer}
                            stopRecording={stopRecording}
                            setActiveClip={setActiveClip}
                            level={level}
                            disableDoneButton={disableDoneButton}
                            doneButtonHandler={handleDoneButton}
                        />

                        <WrapUpDialog
                            open={wrapupOpen}
                            setOpen={setWrapupOpen}
                            doneButtonHandler={handleSimDone}
                            uploadPercent={uploadPercent}
                        />

                        <div className={classes.restartDiv}>
                            <Button
                                variant="contained"
                                disableElevation
                                className={classes.restartButton}
                                startIcon={
                                    <img
                                        src={RestartIcon}
                                        alt="restart"
                                        className={classes.restartIcon}
                                    />
                                }
                                onClick={handleRestart}
                            >
                                Restart
                            </Button>
                            <Button
                                variant="contained"
                                disableElevation
                                className={classes.exitButton}
                                startIcon={<CloseIcon className={classes.exitIcon}/>}
                                onClick={handleExitButton}
                            >
                                Exit
                            </Button>
                        </div>
                    </div>
                </Paper>
                <Divider className={classes.divider}/>
                <div className={classes.footerDiv}>
                    <Grid container className={classes.footerContainer}>
                        <Grid item xs={6}>
                            {simTitle}
                        </Grid>
                        <Grid item xs={6} style={{textAlign: 'end'}}>
                            Practice Mode :
                            {userResponse.mode === "practice" ? (
                                <span className={classes.practiceSpan}> ON</span>
                            ) : (
                                <span className={classes.practiceSpan}> OFF</span>
                            )}
                        </Grid>
                    </Grid>
                </div>
            </div>
        );
    }
    return (
        <div>
            <CircularProgress color="secondary" />
        </div>
    );
};

SimWizard.propTypes = {
    fetchLevelClips: PropTypes.func,
    levelClips: PropTypes.object,
    levels: PropTypes.array,
    simId: PropTypes.string,
    updateStats: PropTypes.func,
    handleViewState: PropTypes.func,
    addAudioToClip: PropTypes.func,
    userResponse: PropTypes.object,
    reset: PropTypes.func,
    //addScreenRecorderBlob: PropTypes.func,
    simTitle: PropTypes.string,
    updateCurrentClip: PropTypes.func,
    restartLevelInTakeSim: PropTypes.func,
    continueSimLevel: PropTypes.bool,
    uploadRecordedVideo: PropTypes.func,
    addAssignmentStatus: PropTypes.func,
    logError: PropTypes.func,
    saveVideoBlob: PropTypes.func,
    captivateWindowRef: PropTypes.object,
    simProductType: PropTypes.string,
    caseValuesArray: PropTypes.object,
    elapsedTime: PropTypes.number,
    userFinalScoreResponse: PropTypes.func,
    config: PropTypes.object,
    setHasVideo: PropTypes.func,

};

const mapStateToProps = (state) => ({
    levelClips: state.sim.levelClips,
    levelClipsTemp: state.sim.levelClipsTemp,
    levels: state.sim.stats.result.levels,
    simId: state.sim.stats.result.sim_id,
    userResponse: state.sim.userResponse,
    simTitle: state.sim.simTitle,
    elapsedTime: state.sim.elapsedTime,
    totalSeconds: state.sim.totalSeconds,
    caseValuesArray: state.sim.caseValuesArray,
    captivateWindowRef: state.sim.captivateWindowRef,
    simProductType: state.sim.currentSimProductType,
});

const mapDispatchToProps = {
    fetchLevelClips,
    updateStats,
    addAudioToClip,
    reset,
    updateCurrentClip,
    restartLevelInTakeSim,
    uploadRecordedVideo,
    userFinalScoreResponse,
    addAssignmentStatus,
    logError,
    saveVideoBlob,
};
export default connect(mapStateToProps, mapDispatchToProps)(SimWizard);

